/**
 * Non-component helpers for the NORA chat panel: the grounded starter-prompt pool
 * + its random sampler, and a compact relative-time formatter for history rows.
 * Kept out of ChatPanel.tsx so that file only exports a component
 * (react-refresh/only-export-components) and so these stay unit-testable — same
 * reason chatSse.ts exists.
 */

// The pool of tappable starter questions shown on the empty panel. Every entry is
// grounded in a real NORA tool (find_station, list_stations, latest_observation,
// station_qc, network_health, station_coverage, nearest_stations, recommend_
// replacement, …) and was adversarially verified as genuinely answerable — no
// invented station codes, no cross-fleet aggregates NORA can't compute. We show a
// small random sample (see pickStarters) so the empty state feels alive and hints
// at the breadth of what NORA can do rather than the same three prompts forever.
export const STARTER_PROMPT_POOL = [
  // Discovery & inventory
  'Pull up the full dossier for CPTR3',
  'Which station is Heathrow?',
  'What network is LAX on?',
  'List every station in Germany',
  'Show all RWIS stations in the US',
  'What data sources do we have?',
  'How big is our station fleet?',
  'Break down our stations by network',
  'How many stations report airTemp?',
  'Show me all the countries we cover',
  // Live observations & extremes
  'Current temperature at Heathrow',
  "What's the wind speed at Heathrow right now?",
  'Show humidity at MBVEN over the last 12 hours',
  'How much rain fell at CPTR3 in 24 hours?',
  'Peak wind gust at CPTR3 overnight',
  // Data quality & QC second-opinions
  "What's the QC pass rate for CPTR3?",
  'Show me the sensor quality of CPTR3 over 30 days',
  'Which parameters are failing QC at MBVEN?',
  'Is 41C at LAX an outlier or sensor error?',
  'How does this dewPoint compare to the 10-year normal?',
  "Does DTN's forecast agree with this temperature reading?",
  'Cross-check this precip reading against the radar map',
  // Network health & lifecycle
  'Which networks are down today?',
  'How healthy is the DWD_SYNOP network?',
  'How fresh is our data across the fleet?',
  'When did MBVEN last report in?',
  'What time period does CPTR3 cover?',
  "How far back does MBVEN's precip record go?",
  // Spatial ops & resilience
  'Nearest active stations within 50 km of LAX',
  'Closest stations to 48.85, 2.35',
  'CPTR3 went offline, find me a replacement',
  'Find an alternative to MBVEN for windSpeed',
];

const STARTER_COUNT = 3; // how many chips to surface at once

/**
 * Pick `count` distinct prompts at random from the pool (Fisher–Yates partial
 * shuffle). Called fresh on each panel open / new chat so the suggestions vary.
 * Falls back to the whole pool if it's ever smaller than `count`.
 */
export const pickStarters = (count = STARTER_COUNT): string[] => {
  const pool = [...STARTER_PROMPT_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(count, pool.length));
};

/**
 * Compact relative timestamp for the history rows ("just now", "3m ago", "2h
 * ago", "Jul 24"). Absolute ms epoch in, human string out.
 */
export const relTime = (ts: number, now: number = Date.now()): string => {
  const diff = now - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
